import React, { useState } from "react";
import { Visible } from "../../../core/components";
import { PriceWarningModal } from "../../../components/modals";
import { useListingDetailsRow } from "../../../components/listing/useListingDetailsRow";
import { LookbookProductInfo } from "./LookbookProductInfo";
import { LookbookToggleList } from "./LookbookToggleList";
import { LookbookActionButtons } from "./LookbookActionButtons";

const IStar = ({ filled }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M8 1.5l1.76 3.57 3.94.57-2.85 2.78.67 3.92L8 10.67l-3.52 1.85.67-3.92-2.85-2.78 3.94-.57L8 1.5z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
      fill={filled ? "currentColor" : "none"}
    />
  </svg>
);

const IEye = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M1.5 8s2.2-4 6.5-4 6.5 4 6.5 4-2.2 4-6.5 4-6.5-4-6.5-4z"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);

const toggleListProps = (props) => ({
  daoLabel: props.daoLabel,
  inStockOn: props.inStockOn,
  onInternalProductChange: props.onInternalProductChange,
  reserved: props.reserved,
  onReserveChange: props.onReserveChange,
  ebay: props.ebay,
  onSyncEbayChange: props.onSyncEbayChange,
  chrono: props.chrono,
  onSyncChronoChange: props.onSyncChronoChange,
  watchFinder: props.watchFinder,
  onSyncWatchFinderChange: props.onSyncWatchFinderChange,
  isActiveListing: props.isActiveListing,
});

export function LookbookListingCard({
  listing,
  selected = false,
  onSelectToggle,
}) {
  const row = useListingDetailsRow({ listing });

  const {
    product,
    isShopify,
    isNetwork,
    editButtonProps,
    daoLabel,
    inStockOn,
    reserved,
    ebay,
    chrono,
    watchFinder,
    isActiveListing,
    onInternalProductChange,
    onReserveChange,
    onSyncEbayChange,
    onSyncChronoChange,
    onSyncWatchFinderChange,
    showPriceWarningModal,
    pendingToggleType,
    handleModalEdit,
    handleModalConfirm,
    closeModal,
  } = row;

  const onStarClick = (e) => {
    e.stopPropagation();
    onSelectToggle?.();
  };

  const [actionMenuOpen, setActionMenuOpen] = useState(false);

  // Below the hooks: `deleted` flips to true on a card that is already
  // mounted (that is the whole point of deleting one), so an early return
  // above this useState dropped a hook mid-life and took the grid with it.
  if (row.deleted) return null;

  const likeCount = listing.likeCount ?? 0;
  const viewCount = listing.viewCount ?? 0;
  const toggles = toggleListProps({
    daoLabel,
    inStockOn,
    onInternalProductChange,
    reserved,
    onReserveChange,
    ebay,
    onSyncEbayChange,
    chrono,
    onSyncChronoChange,
    watchFinder,
    onSyncWatchFinderChange,
    isActiveListing,
  });

  const imageOverlay = (
    <div className="lb-product-info__image-overlay" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={`lb-lcard__icon-btn lb-lcard__icon-btn--ghost${
          selected ? " lb-lcard__icon-btn--active" : ""
        }`}
        onClick={onStarClick}
        aria-label={selected ? "Remove from lookbook" : "Add to lookbook"}
        aria-pressed={selected}
      >
        <IStar filled={selected} />
      </button>
      <button
        type="button"
        className="lb-lcard__icon-btn lb-lcard__icon-btn--ghost"
        aria-label={`${viewCount} views`}
        title={`${viewCount} views`}
      >
        <span className="lb-lcard__overlay-count">{viewCount}</span>
        <IEye />
      </button>
    </div>
  );

  return (
    <>
      <article className={`lb-lcard${actionMenuOpen ? " lb-lcard--menu-open" : ""}`}>
        {/* Middle — product info (+ mobile image overlays) */}
        <div className="lb-lcard__middle">
          <LookbookProductInfo
            listing={listing}
            product={product}
            imageOverlay={imageOverlay}
          />
        </div>

        {/* Bottom — toggles + actions */}
        <Visible when={!isNetwork && !isShopify}>
          <div className="lb-lcard__bottom">
            <div className="lb-lcard__stock-col">
              <LookbookToggleList {...toggles} variant="stock" />
            </div>

            <div className="lb-lcard__marketplace-col">
              <LookbookToggleList {...toggles} variant="marketplace" />
            </div>

            <div className="lb-lcard__mobile-toggles">
              <LookbookToggleList {...toggles} variant="full" compact />
            </div>

            <div className="lb-lcard__actions-col">
              <div className="lb-lcard__desktop-controls" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className={`lb-lcard__stat-btn${selected ? " lb-lcard__stat-btn--active" : ""}`}
                  onClick={onStarClick}
                  aria-label={selected ? "Remove from lookbook" : "Add to lookbook"}
                  aria-pressed={selected}
                >
                  <span className="lb-lcard__stat-count">{likeCount}</span>
                  <IStar filled={selected} />
                </button>
                <button
                  type="button"
                  className="lb-lcard__stat-btn"
                  aria-label={`${viewCount} views`}
                  title={`${viewCount} views`}
                >
                  <span className="lb-lcard__stat-count">{viewCount}</span>
                  <IEye />
                </button>
              </div>

              <LookbookActionButtons
                editButtonProps={editButtonProps}
                listing={listing}
                onMenuOpenChange={setActionMenuOpen}
              />
            </div>
          </div>
        </Visible>
      </article>

      <PriceWarningModal
        open={showPriceWarningModal}
        closeModal={closeModal}
        type={pendingToggleType}
        onEdit={handleModalEdit}
        onConfirm={handleModalConfirm}
      />
    </>
  );
}
